// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomDatePickerComponent } from '@way/theme-host/components/custom-date-picker/custom-date-picker.component';

export interface BookingLayout1Data {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  backgroundColor?: string;
  // formFields removed - form structure is static in the layout component
}

/**
 * Preview component for "Booking Layout 1" – renders a fixed-structure booking form and manages user input, date/time pairing, and form submission.
 * @component
 * @selector app-booking-layout-1-preview
 * @example
 *   <app-booking-layout-1-preview
 *     [data]="sampleLayoutData"
 *     [formData]="initialFormData"
 *     [primaryColor]="'#ff0000'"
 *     (formSubmit)="handleSubmit($event)"
 *     (fieldChange)="handleFieldChange($event)"
 *     (dateTimeChange)="handleDateTimeChange($event)">
 *   </app-booking-layout-1-preview>
 *
 * Description:
 * This standalone Angular preview component provides a fixed booking form layout (full name, email, phone, date/time, and special requests).
 * It consolidates form state locally (bookingFormData) while allowing initial values via formData input and notifying the parent through events.
 * The component includes helpers to compute form rows (two-column layout with textarea spanning full width), handle date/time pairing (date field + associated time field), and manage value retrieval and updates.
 * Key methods:
 *   - getFormFields(): returns the static form field definitions for this layout.
 *   - getFormRows(): arranges fields into rows (two fields per row; textarea in its own row).
 *   - getSelectOptions(field): returns select options for a field.
 *   - isDateField / isTimeField / shouldUseDatePicker: helpers for date/time field detection and UI decisions.
 *   - getTimeFieldForDate(dateField): finds an associated time field for a given date field by name matching.
 *   - shouldHideTimeField(currentField): determines if a time field should be hidden due to an associated date field.
 *   - getFieldValue / setFieldValue: read/write values from internal bookingFormData (merging with provided formData) and emit fieldChange.
 *   - onFieldInput / onFieldChange: input handlers for text/textarea/select elements.
 *   - getDateTimeValue(dateField): composes a Date object from separate date and time fields.
 *   - onDateTimeChange(dateField, dateTime): sets date and time string values, updates internal state, and emits dateTimeChange.
 *   - onFormSubmit(event): prevents default, consolidates form data and emits formSubmit with the event.
 *   - onFormClick(event): prevents unwanted propagation except for clicks inside date picker elements.
 *   - trackByRowIndex / trackByFieldId: trackBy helpers for ngFor.
 * Lifecycle hooks:
 *   - This component does not rely on special Angular lifecycle hooks for core behavior; initialization is handled via inputs and internal default state.
 *
 * @input {BookingLayout1Data | null} data - Layout content from API (titles, descriptions, button text and any field metadata); optional.
 * @input {string | null} headingColor - Optional heading color override for preview styling.
 * @input {string | null} paragraphColor - Optional paragraph/body text color override.
 * @input {string | null} headingFont - Optional heading font family override.
 * @input {string | null} bodyFont - Optional body font family override.
 * @input {string | null} primaryColor - Optional primary/accent color used for buttons and highlights.
 * @input {Record<string, any>} formData - Optional initial form values keyed by field name/id; merged with internal bookingFormData.
 *
 * @output {{ formData: Record<string, any>; event: Event }} formSubmit - Emitted when the form is submitted with consolidated form data and the original submit event.
 * @output {{ field: any; value: any }} fieldChange - Emitted whenever a single field value is changed; provides the field definition and new value.
 * @output {{ field: any; dateTime: Date | null }} dateTimeChange - Emitted when a date/time value is changed; provides the date field definition and the composed Date (or null).
 */
@Component({
  selector: 'app-booking-layout-1-preview',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomDatePickerComponent],
  templateUrl: './booking-layout-1.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./booking-layout-1.component.scss'],
})
export class BookingLayout1PreviewComponent {
  constructor(private logger: LoggerService) {}

  @Input() data: BookingLayout1Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() formData: Record<string, any> = {};
  @Output() formSubmit = new EventEmitter<{ formData: Record<string, any>; event: Event }>();
  @Output() fieldChange = new EventEmitter<{ field: any; value: any }>();
  @Output() dateTimeChange = new EventEmitter<{ field: any; dateTime: Date | null }>();

  // Form data storage
  private bookingFormData: Record<string, any> = {};

  /**
   * Get form fields - static default fields since form structure is fixed in layout
   */
  getFormFields(): any[] {
    // Return static form fields - the layout has a fixed structure
    // Only title, description, and button text are dynamic from API
    return [
      {
        id: 'full_name',
        name: 'full_name',
        label: 'Full Name',
        type: 'text',
        placeholder: 'Enter your full name',
        required: true,
      },
      {
        id: 'email',
        name: 'email',
        label: 'Email Address',
        type: 'email',
        placeholder: 'Enter your email',
        required: true,
      },
      {
        id: 'phone',
        name: 'phone',
        label: 'Phone Number',
        type: 'tel',
        placeholder: 'Enter your phone number',
        required: true,
      },
      {
        id: 'date',
        name: 'date',
        label: 'Date',
        type: 'date',
        placeholder: 'Select date and time',
        required: true,
      },
      {
        id: 'special_requests',
        name: 'special_requests',
        label: 'Special Requests or Notes',
        type: 'textarea',
        placeholder: 'Any special request or vehicle details',
        required: false,
      },
    ];
  }

  /**
   * Get form rows (2 fields per row, except textarea which spans full width)
   */
  getFormRows(): any[][] {
    const fields = this.getFormFields();
    if (!fields || fields.length === 0) return [];

    const rows: any[][] = [];
    let i = 0;

    while (i < fields.length) {
      const currentField = fields[i];

      // If it's a textarea, put it in its own row
      if (currentField?.type === 'textarea') {
        rows.push([currentField]);
        i++;
      } else {
        // For other fields, try to pair them (2 per row)
        const row = fields.slice(i, i + 2);
        rows.push(row);
        i += row.length;
      }
    }

    return rows;
  }

  /**
   * Get select options for a field
   */
  getSelectOptions(field: any): { value: string; label: string; disabled?: boolean }[] {
    if (!field) return [];
    return field.options || [];
  }

  /**
   * Check if field is a date field
   */
  isDateField(field: any): boolean {
    return field?.type === 'date' || field?.type === 'datetime' || field?.type === 'datetime-local';
  }

  /**
   * Check if field is a time field
   */
  isTimeField(field: any): boolean {
    return field?.type === 'time';
  }

  /**
   * Check if field should use date picker
   */
  shouldUseDatePicker(field: any): boolean {
    if (!field) return false;
    return this.isDateField(field) || field?.type === 'datetime-local';
  }

  /**
   * Get time field for a date field
   */
  getTimeFieldForDate(dateField: any): any | null {
    if (!dateField) return null;
    const fields = this.getFormFields();
    const dateFieldName = (dateField?.name || dateField?.id || '').toLowerCase();

    return (
      fields.find(f => {
        if (!this.isTimeField(f)) return false;
        const timeFieldName = (f.name || f.id || '').toLowerCase();
        const baseName = dateFieldName.replace('date', '').replace('_', '').trim();
        const timeBaseName = timeFieldName.replace('time', '').replace('_', '').trim();
        return (
          baseName === timeBaseName ||
          timeFieldName.includes(baseName) ||
          dateFieldName.includes(timeBaseName)
        );
      }) || null
    );
  }

  /**
   * Check if time field should be hidden
   */
  shouldHideTimeField(currentField: any): boolean {
    if (!this.isTimeField(currentField)) return false;
    const fields = this.getFormFields();
    return fields.some(f => {
      if (this.isDateField(f)) {
        const associatedTimeField = this.getTimeFieldForDate(f);
        return (
          associatedTimeField &&
          (associatedTimeField.id === currentField.id ||
            associatedTimeField.name === currentField.name)
        );
      }
      return false;
    });
  }

  /**
   * Get field value
   */
  getFieldValue(field: any): any {
    const fieldKey = field?.name || field?.id;
    return this.bookingFormData[fieldKey] || this.formData[fieldKey] || '';
  }

  /**
   * Set field value
   */
  setFieldValue(field: any, value: any): void {
    const fieldKey = field?.name || field?.id;
    this.bookingFormData[fieldKey] = value;
    this.fieldChange.emit({ field, value });
  }

  /**
   * Handle field input
   */
  onFieldInput(field: any, event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    if (target) {
      this.setFieldValue(field, target.value);
    }
  }

  /**
   * Handle field change (select)
   */
  onFieldChange(field: any, event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (target) {
      this.setFieldValue(field, target.value);
    }
  }

  /**
   * Get date/time value
   */
  getDateTimeValue(dateField: any): Date | null {
    try {
      if (!dateField) return null;
      const timeField = this.getTimeFieldForDate(dateField);

      const dateKey = dateField?.name || dateField?.id;
      const dateValue = this.getFieldValue({ name: dateKey, id: dateKey });
      const timeValue = timeField ? this.getFieldValue(timeField) : null;

      if (!dateValue) return null;

      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return null;

      if (timeValue) {
        const [hours, minutes] = timeValue.split(':');
        if (hours && minutes) {
          date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        }
      }
      return date;
    } catch {
      return null;
    }
  }

  /**
   * Handle date/time change
   */
  onDateTimeChange(dateField: any, dateTime: Date | null): void {
    setTimeout(() => {
      try {
        if (!dateField) return;
        const timeField = this.getTimeFieldForDate(dateField);
        const dateKey = dateField?.name || dateField?.id;

        if (!dateTime) {
          this.setFieldValue({ name: dateKey, id: dateKey }, '');
          if (timeField) {
            const timeKey = timeField?.name || timeField?.id;
            this.setFieldValue({ name: timeKey, id: timeKey }, '');
          }
          return;
        }

        const dateStr = dateTime.toISOString().split('T')[0];
        this.setFieldValue({ name: dateKey, id: dateKey }, dateStr);

        if (timeField) {
          const timeKey = timeField?.name || timeField?.id;
          const hours = dateTime.getHours().toString().padStart(2, '0');
          const minutes = dateTime.getMinutes().toString().padStart(2, '0');
          this.setFieldValue({ name: timeKey, id: timeKey }, `${hours}:${minutes}`);
        }

        this.dateTimeChange.emit({ field: dateField, dateTime });
      } catch (error) {
        this.logger.error('Error setting date/time value:', error);
      }
    }, 0);
  }

  /**
   * Handle form submission
   */
  onFormSubmit(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.formSubmit.emit({ formData: { ...this.formData, ...this.bookingFormData }, event });
  }

  /**
   * Handle form click
   */
  onFormClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    const isDatePickerClick =
      target.closest('.custom-date-picker') !== null ||
      target.closest('.date-picker-wrapper') !== null ||
      target.closest('.date-picker-input') !== null ||
      target.closest('.date-picker-dropdown') !== null ||
      target.classList.contains('custom-date-picker') ||
      target.classList.contains('date-picker-input') ||
      target.classList.contains('date-picker-dropdown');

    if (!isDatePickerClick) {
      event.stopPropagation();
    }
  }

  /**
   * Track by function for rows
   */
  trackByRowIndex(index: number): number {
    return index;
  }

  /**
   * Track by function for fields
   */
  trackByFieldId(index: number, field: any): string {
    return field?.id || field?.name || index.toString();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
